// API-keys table columns — rendered inside the common DataTable in
// ApiKeysModal, per the design: name, description, status chip, a masked
// key, who created / revoked it, when, and the row kebab.
//
// The Key cell is a static row of dots: the backend never returns the key
// after creation (only its hash is stored), so the cell is decoration that
// says "there is a secret here", not data — nothing is fetched for it.

import { useTranslation } from 'react-i18next';
import { Avatar, Chip, type DataTableColumn } from '@/components/common';
import { ApiKeyStatus, type ApiKeyItem } from '@/api/endpoints/apiKeysApi';
import { fmtDateTime } from '@/utils/formatDate';
import initialsOf from '@/utils/initials';
import toneOf from '@/utils/avatarTone';
import ApiKeyRowActions from './ApiKeyRowActions';

const KEY_MASK = '••••••••••';

const dash = <span className='forms-table__muted'>—</span>;
/** The app-wide person cell: initials avatar + name (the Forms table idiom). */
const person = (name: string | null) =>
  name ? (
    <span className='forms-table__person'>
      <Avatar initials={initialsOf(name)} size={28} tone={toneOf(name)} />
      <span>{name}</span>
    </span>
  ) : (
    dash
  );
const when = (iso: string | null) =>
  iso ? <span className='api-keys__when'>{fmtDateTime(iso)}</span> : dash;

interface UseApiKeyColumnsArgs {
  onRevoke: (row: ApiKeyItem) => void;
  onDelete: (row: ApiKeyItem) => void;
}

export const useApiKeyColumns = ({
  onRevoke,
  onDelete,
}: UseApiKeyColumnsArgs): DataTableColumn<ApiKeyItem>[] => {
  const { t } = useTranslation();

  return [
    {
      key: 'name',
      label: t('api_keys_col_name'),
      width: 'minmax(10rem, 1.2fr)',
      render: (r) => <span className='api-keys__name'>{r.name}</span>,
    },
    {
      key: 'description',
      label: t('api_keys_col_description'),
      width: 'minmax(14rem, 2fr)',
      render: (r) =>
        r.description ? (
          <span className='api-keys__description' title={r.description}>
            {r.description}
          </span>
        ) : (
          dash
        ),
    },
    {
      key: 'status',
      label: t('api_keys_col_status'),
      width: '7.5rem',
      render: (r) =>
        r.status === ApiKeyStatus.Active ? (
          <Chip variant='success' dot>
            {t('api_keys_status_active')}
          </Chip>
        ) : (
          <Chip variant='danger' dot>
            {t('api_keys_status_revoked')}
          </Chip>
        ),
    },
    {
      key: 'key',
      label: t('api_keys_col_key'),
      width: '10rem',
      render: () => (
        <span className='api-keys__key-mask' aria-hidden='true'>
          {KEY_MASK}
        </span>
      ),
    },
    {
      key: 'createdBy',
      label: t('api_keys_col_created_by'),
      width: 'minmax(9rem, 1fr)',
      render: (r) => person(r.createdBy),
    },
    {
      key: 'revokedBy',
      label: t('api_keys_col_revoked_by'),
      width: 'minmax(9rem, 1fr)',
      render: (r) => person(r.revokedBy),
    },
    {
      key: 'createdAt',
      label: t('api_keys_col_created_at'),
      width: 'minmax(11rem, 1fr)',
      render: (r) => when(r.createdAt),
    },
    {
      key: 'revokedAt',
      label: t('api_keys_col_revoked_at'),
      width: 'minmax(11rem, 1fr)',
      render: (r) => when(r.revokedAt),
    },
    {
      key: 'actions',
      label: '',
      width: '3.5rem',
      render: (r) => (
        <ApiKeyRowActions
          onRevoke={r.status === ApiKeyStatus.Active ? () => onRevoke(r) : undefined}
          onDelete={r.status === ApiKeyStatus.Revoked ? () => onDelete(r) : undefined}
        />
      ),
    },
  ];
};
